import { useState } from "react";
import { Copy } from "lucide-react";
import { Banner, Button, useToast } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { getInviteAbsoluteUrl } from "@/lib/routing/portal-urls";
import { copyToClipboard } from "./helpers";
import type { ReissuedInvite } from "./useInviteReissue";

const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

/**
 * The reissued invite, details collapsed by default: the token is a secret
 * and the admin usually only needs to copy the link. A failed reissue shows
 * here, where the invite would have appeared, with Try again.
 */
export function InvitePanel({
  invite,
  error,
  locale,
  onRetry,
  retrying,
}: {
  invite: ReissuedInvite | null;
  /** The failed reissue's detail ("" when the server said nothing). */
  error: string | null;
  locale: string;
  onRetry: () => void;
  retrying: boolean;
}) {
  const t = useTranslations();
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [shownFor, setShownFor] = useState(invite);
  // A newly reissued invite starts collapsed again.
  if (invite !== shownFor) {
    setShownFor(invite);
    setIsOpen(false);
  }

  if (error !== null) {
    return (
      <div style={{ margin: "0 20px 16px" }}>
        <Banner
          tone="error"
          title={t("admin.inviteReissueFailedTitle")}
          actions={
            <Button size="sm" loading={retrying} loadingLabel={t(keys.shell.retrying)} onClick={onRetry}>
              {t(keys.shell.retry)}
            </Button>
          }
        >
          {[error, t("admin.inviteReissueFailedBody")].filter(Boolean).join(" ")}
        </Banner>
      </div>
    );
  }
  if (!invite) return null;

  const inviteLink =
    typeof window !== "undefined"
      ? getInviteAbsoluteUrl(
          locale,
          invite.inviteToken,
          window.location.protocol === "http:" ? "http" : "https",
        )
      : "";

  return (
    <div
      style={{
        margin: "0 20px 16px",
        padding: "14px 16px",
        borderRadius: 10,
        background: "var(--rd-surface-soft)",
        boxShadow: "inset 0 0 0 1px var(--rd-line-header)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: "var(--rd-text-meta)",
            flex: 1,
          }}
        >
          {t(keys.admin.inviteToken)}
        </span>
        <Button
          variant="ghost"
          size="sm"
          aria-expanded={isOpen}
          aria-controls="reissue-invite-details"
          onClick={() => setIsOpen((current) => !current)}
        >
          {isOpen ? t(keys.admin.inviteDetailsHide) : t(keys.admin.inviteDetailsShow)}
        </Button>
        {inviteLink ? (
          <Button
            variant="secondary"
            size="sm"
            icon={Copy}
            aria-label={t(keys.common.copyInviteLink)}
            onClick={() => {
              void copyToClipboard(inviteLink).then((copied) => {
                if (copied) toast.show({ tone: "neutral", icon: Copy, message: t(keys.admin.inviteLinkCopiedToast) });
              });
            }}
          >
            {t(keys.common.copyInviteLink)}
          </Button>
        ) : null}
      </div>
      {isOpen ? (
        <dl id="reissue-invite-details" className="rd-kv" style={{ fontSize: 12 }}>
          <dt>{t(keys.admin.inviteToken)}</dt>
          <dd style={{ fontFamily: MONO, overflowWrap: "anywhere" }}>
            <bdi dir="ltr">{invite.inviteToken}</bdi>
          </dd>
          {inviteLink ? (
            <>
              <dt>{t("admin.inviteLinkLabel")}</dt>
              <dd style={{ fontFamily: MONO, overflowWrap: "anywhere" }}>
                <bdi dir="ltr">{inviteLink}</bdi>
              </dd>
            </>
          ) : null}
          <dt>{t(keys.admin.inviteExpires)}</dt>
          <dd style={{ fontFamily: MONO }}>
            <bdi dir="ltr">{invite.expiresAt}</bdi>
          </dd>
        </dl>
      ) : (
        <span style={{ fontSize: 13, color: "var(--rd-text-secondary)" }}>
          {t(keys.admin.inviteDetailsCollapsedHint)}
        </span>
      )}
    </div>
  );
}
